import { CompType, RecommendedCompsStatistics } from '@aloft/toolkit-shared';
import polyline from '@mapbox/polyline';
import { Image, Text, View } from '@react-pdf/renderer';

import { Marker, PinColors, getMapboxStaticURL } from '../../../utils/getMapboxStaticUrl.ts';
import ActiveIcon from '../assets/mapMarkers/active.png';
import PendingIcon from '../assets/mapMarkers/pending.png';
import SoldIcon from '../assets/mapMarkers/sold.png';
import { Tkv2PDFPage } from '../components/Tkv2PDFPage.tsx';
import { fontStyles, salesComparisonMapPageStyles as styles } from '../styles.ts';
import { CompDataForMap, Coordinate, SalesComparisonMapPageProps } from '../types.ts';

const getMarkerColor = (mlsStatus?: string) => {
  switch (mlsStatus) {
    case 'SettledSale':
      return PinColors.SOLD;
    case 'Active':
      return PinColors.ACTIVE;
    case 'Contract':
      return PinColors.PENDING;
    default:
      return PinColors.OFF_MARKET;
  }
};

const renderMapPage = (
  comps: CompDataForMap[],
  subjectAddress: string,
  subjectLatitude?: number,
  subjectLongitude?: number,
  mapBoxCoordinates?: Coordinate[],
  statistics?: RecommendedCompsStatistics,
  badge?: CompType,
) => {
  const allCoordinates = [
    ...(subjectLatitude && subjectLongitude
      ? [{ lat: subjectLatitude, lon: subjectLongitude }]
      : []),
    ...comps.map((comp) => ({ lat: comp.latitude, lon: comp.longitude })),
  ];

  const centerLat =
    allCoordinates.reduce((sum, coord) => sum + coord.lat, 0) / allCoordinates.length;
  const centerLng =
    allCoordinates.reduce((sum, coord) => sum + coord.lon, 0) / allCoordinates.length;

  const buffer = 0.0025; // ~250m buffer

  const minLat = Math.min(...allCoordinates.map((c) => c.lat)) - buffer;
  const minLon = Math.min(...allCoordinates.map((c) => c.lon)) - buffer;
  const maxLat = Math.max(...allCoordinates.map((c) => c.lat)) + buffer;
  const maxLon = Math.max(...allCoordinates.map((c) => c.lon)) + buffer;

  const boxCoords = [
    [minLat, minLon],
    [maxLat, minLon],
    [maxLat, maxLon],
    [minLat, maxLon],
    [minLat, minLon],
  ];

  // If a polygon is provided (from Recommended Comps), use it for the overlay; otherwise, use the default rectangle
  let boundedBox: string;
  if (mapBoxCoordinates && mapBoxCoordinates.length > 0) {
    // mapbox polyline expects [lat, lng]; our input is [lng, lat]
    const latLonPolygon = mapBoxCoordinates.map(
      ({ longitude, latitude }) => [latitude, longitude] as [number, number],
    );
    // // ensure closed shape
    // const first = latLonPolygon[0];
    // const last = latLonPolygon[latLonPolygon.length - 1];
    // if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
    //   latLonPolygon.push(first);
    // }
    boundedBox = polyline.encode(latLonPolygon);
  } else {
    boundedBox = polyline.encode(boxCoords as [number, number][]);
  }

  const markers: Marker[] = [
    ...(subjectLatitude && subjectLongitude
      ? [
        {
          lat: subjectLatitude,
          lon: subjectLongitude,
          label: 'home',
          color: PinColors.SUBJECT,
          name: 'pin-l' as const,
        },
      ]
      : []),
    ...comps.map((comp, index) => ({
      lat: comp.latitude,
      lon: comp.longitude,
      label: `${comp.position ? comp.position : index + 1}`,
      color: getMarkerColor(comp.mlsStatus),
      name: 'pin-s' as const,
    })),
  ];

  const mapTypes = ['road', 'satellite'] as const;
  const [streetMapUrl, satelliteMapUrl] = mapTypes.map((type) =>
    getMapboxStaticURL(
      null,
      null,
      `${styles.mapContainer.width}x${styles.mapContainer.height}`,
      type,
      markers,
      boundedBox,
    ),
  );

  return (
    <Tkv2PDFPage title="Sales Comparison" address={subjectAddress} badge={badge}>
      <View style={styles.summaryContainer}>
        <View style={styles.summaryItemContainer}>
          <View style={styles.summaryItem}>
            <Image src={SoldIcon} style={styles.summaryIcon} />
            <Text style={fontStyles.body}>
              Sold: <Text style={fontStyles.bodyBold}>{statistics?.listingsSoldCount ?? '-'}</Text>
            </Text>
          </View>
          <View style={styles.summaryItem}>
            <Image src={ActiveIcon} style={styles.summaryIcon} />
            <Text style={fontStyles.body}>
              Active:{' '}
              <Text style={fontStyles.bodyBold}>{statistics?.listingsActiveCount ?? '-'}</Text>
            </Text>
          </View>
          <View style={styles.summaryItem}>
            <Image src={PendingIcon} style={styles.summaryIcon} />
            <Text style={fontStyles.body}>
              Pending:{' '}
              <Text style={fontStyles.bodyBold}>{statistics?.listingsPendingCount ?? '-'}</Text>
            </Text>
          </View>
        </View>
        <View style={styles.summaryItemContainer}>
          <Text style={fontStyles.body}>Sales in the last:</Text>
          <Text style={fontStyles.body}>
            0-3 months:&nbsp;
            <Text style={fontStyles.bodyBold}>{statistics?.last3MonthsListingsCount ?? '-'}</Text>
          </Text>
          <Text style={fontStyles.body}>
            4-6 months:&nbsp;
            <Text style={fontStyles.bodyBold}>{statistics?.last6MonthsListingsCount ?? '-'}</Text>
          </Text>
          <Text style={fontStyles.body}>
            7-12 months:&nbsp;
            <Text style={fontStyles.bodyBold}>{statistics?.last12MonthsListingsCount ?? '-'}</Text>
          </Text>
        </View>
      </View>
      <View style={styles.mapContainer}>
        {[satelliteMapUrl, streetMapUrl].map(
          (url) =>
            url && (
              <Image
                key={url}
                src={url}
                style={{
                  width: styles.mapContainer.width,
                  height: styles.mapContainer.height,
                  objectFit: 'cover',
                  border: '1px solid #ccc',
                  borderRadius: 4,
                }}
              />
            ),
        )}
      </View>
    </Tkv2PDFPage>
  );
};

export const SalesComparisonMapPage = ({
  subjectAddress,
  comps,
  subjectLatitude,
  subjectLongitude,
  mapBoxCoordinates,
  statistics,
  compSequence,
  compTypes,
  hasArvOption,
}: SalesComparisonMapPageProps) => {
  if (!hasArvOption) {
    return (
      <>
        {renderMapPage(
          comps,
          subjectAddress,
          subjectLatitude,
          subjectLongitude,
          mapBoxCoordinates,
          statistics,
        )}
      </>
    );
  }

  const aivComps: CompDataForMap[] = [];
  const arvComps: CompDataForMap[] = [];

  const compsById = new Map(comps.map((comp) => [comp.id, comp]));

  compSequence.forEach((compId, index) => {
    const compType = compTypes[compId];
    const compData = compsById.get(compId);
    if (compData) {
      if (compType === 'arv') {
        arvComps.push({
          ...compData,
          position: index + 1,
        });
      } else if (compType === 'aiv') {
        aivComps.push({
          ...compData,
          position: index + 1,
        });
      }
    }
  });

  return (
    <>
      {renderMapPage(
        aivComps,
        subjectAddress,
        subjectLatitude,
        subjectLongitude,
        mapBoxCoordinates,
        statistics,
        'aiv',
      )}
      {renderMapPage(
        arvComps,
        subjectAddress,
        subjectLatitude,
        subjectLongitude,
        mapBoxCoordinates,
        statistics,
        'arv',
      )}
    </>
  );
};
